import React, { useState, useEffect, useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import TGButton from 'src/components/ui/TGButton';
import { FlexRow } from 'src/utils/flexLayouts';
import Select from 'src/components/ui/form/select';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import ModalAddComment from './modalAddComment/ModalAddComment';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { addData, deleteData, fetchData } from '../../actions/actions';
import { selectLastAddedId, selectLastDocId, selectModelData } from '../../selectors/selectors';
import CommentsDisplay from '../CommentsDisplay/CommentsDisplay';
import { PlanActionsContext } from '../../types/types';

interface AddCommentGopProps {
  toggleDoing: (value: boolean) => void;
  isDisabled?: boolean;
  isAutoSave?: boolean;
  context: PlanActionsContext;
  firstCommentId?: number;
  onCommentAdded?: () => void;
}

export default function AddCommentGop({ toggleDoing, isDisabled, isAutoSave, firstCommentId, context, onCommentAdded }: AddCommentGopProps) {
  const dispatch = useAppDispatch();
  const dataDirection = useAppSelector((state: any) => selectModelData(state, 'gop-directions'));
  const dataBlock = useAppSelector((state: any) => selectModelData(state, 'gop-blocks'));
  const dataDemands = useAppSelector((state: any) => selectModelData(state, 'gop-requirements'));

  const lastIdGopComments = useAppSelector((state) => selectLastAddedId(state, 'gop-comments'));
  const lastDocId = useAppSelector((state) => selectLastDocId(state, 'gop-comments'));

  const [filteredBlocks, setFilteredBlocks] = useState<any[]>([]);
  const [filteredDemands, setFilteredDemands] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitialSaveComplete, setIsInitialSaveComplete] = useState(false);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [isAutoFillComplete, setIsAutoFillComplete] = useState(false);

  const { selectedGroup, selectedFactory, selectedGop, currentQuarter, currentYear, currentBlock, currentDirection, currentRequirement } = context;

  const modelNames = ['gop-directions', 'gop-blocks', 'gop-requirements'];

  useEffect(() => {
    modelNames.forEach((modelName) => {
      dispatch(fetchData(modelName));
    });
  }, [dispatch]);

  // console.log('lastDocId',lastDocId);
  const cancel = useCallback(() => {
    if (isAutoSave) {
      toggleDoing(false);
      return;
    }

    if (lastDocId && !firstCommentId) {
      dispatch(deleteData('gop-comments', lastDocId));
    }

    toggleDoing(false);
  }, [lastDocId, firstCommentId, dispatch, toggleDoing, isAutoSave, onCommentAdded]);

  const defaultValues = {
    direction: null,
    block: null,
    demand: null,
  };

  const methods = useForm<any>({
    defaultValues: defaultValues,
  });

  const { handleSubmit, control, formState, watch } = methods;

  const selectedDirection = watch('direction');
  const selectedBlock = watch('block');

  //! предзаполненность формы из контекста
  useEffect(() => {
    let fieldsSet = 0;
    const totalFields = (currentDirection ? 1 : 0) + (currentBlock ? 1 : 0) + (currentRequirement ? 1 : 0);

    if (currentDirection && !selectedDirection) {
      methods.setValue('direction', {
        id: currentDirection.id,
        name: currentDirection.Title,
      });
      fieldsSet++;
    }

    if (currentBlock && !selectedBlock) {
      methods.setValue('block', {
        id: currentBlock.id,
        name: currentBlock.title,
      });
      fieldsSet++;
    }

    if (currentRequirement) {
      const currentDemand = watch('demand');
      if (!currentDemand) {
        methods.setValue('demand', {
          id: currentRequirement.id,
          name: currentRequirement.title,
          isFocal: currentRequirement.isFocal,
        });
        fieldsSet++;
      }
    }

    if (fieldsSet === totalFields && totalFields > 0) {
      setIsAutoFillComplete(true);
    }
  }, [currentDirection, currentBlock, currentRequirement, selectedDirection, selectedBlock, methods]);

  //! При изменении направления показываю блоки (как в действиях)
  useEffect(() => {
    if (selectedDirection && dataDirection) {
      const selectedDirectionData = dataDirection.find((dir: any) => dir.id === selectedDirection.id);
      if (selectedDirectionData && selectedDirectionData.blocks && Array.isArray(selectedDirectionData.blocks)) {
        const blocksFromDirection = selectedDirectionData.blocks.map((block: any) => ({
          id: block.id,
          name: block.Title || block.title || '',
        }));
        setFilteredBlocks(blocksFromDirection);
      }
      if (!currentBlock) {
        methods.setValue('block', null);
        methods.setValue('demand', null);
      }
    } else {
      setFilteredBlocks([]);
    }
  }, [selectedDirection, dataDirection, dataBlock, currentBlock, methods]);

  //! При изменении блока показываю требования (как в действиях)
  useEffect(() => {
    if (selectedBlock && dataBlock) {
      const selectedBlockData = dataBlock.find((block: any) => block.id === selectedBlock.id);
      if (selectedBlockData && selectedBlockData.requirements && Array.isArray(selectedBlockData.requirements)) {
        const demandsFromBlock = selectedBlockData.requirements.map((req: any) => ({
          id: req.id,
          name: req.Title || req.title || '',
          isFocal: req.isFocal || false,
        }));
        setFilteredDemands(demandsFromBlock);
      }
      if (!currentRequirement) {
        methods.setValue('demand', null);
      }
    } else {
      setFilteredDemands([]);
    }
  }, [selectedBlock, dataBlock, dataDemands, currentRequirement, methods]);

  //! австосейв при октрытии предзаполненной формы
  useEffect(() => {
    if (!isAutoFillComplete) return;
    if (firstCommentId) {
      setIsInitialSaveComplete(true);
      return;
    }

    const timer = setTimeout(() => {
      const currentValues = methods.getValues();
      const allFieldsFilled = currentValues.direction && currentValues.block && currentValues.demand;

      if (!isInitialSaveComplete && allFieldsFilled) {
        const initialData = {
          directionID: currentValues.direction?.id,
          directionTitle: currentValues.direction?.name,
          blockID: currentValues.block?.id,
          blockTitle: currentValues.block?.name,
          demandID: currentValues.demand?.id,
          demandTitle: currentValues.demand?.name,
          group: selectedGroup?.id,
          factory: selectedFactory.id,
          gop: selectedGop.id,
          quarter: currentQuarter,
          year: currentYear,
        };
        dispatch(addData('gop-comments', initialData));
        setIsInitialSaveComplete(true);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isAutoFillComplete, isInitialSaveComplete, firstCommentId, dispatch, methods, selectedGroup, selectedFactory, selectedGop, currentQuarter, currentYear]);

  const onSubmit = (data: any) => {
    if (onCommentAdded) {
      onCommentAdded();
    }
    toggleDoing(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCommentsRefreshKey((prev) => prev + 1);
  }, []);

  const actualCommentId = firstCommentId || lastIdGopComments;

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-5'>
            <section className='grid grid-cols-2 gap-5'>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: 'выберите направление',
                        type: 'select',
                        require: true,
                        title: 'Направление',
                        width: 'w-full',
                        key: 'direction',
                        single: true,
                        disabled: isDisabled,
                        options:
                          dataDirection?.map((item: any) => ({
                            id: item.id,
                            name: item.Title,
                          })) || [],
                      }}
                      errors={{ direction: !!error }}
                    />
                  )}
                  name='direction'
                  control={control}
                />
              </div>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: !selectedDirection ? 'Сначала выберите направление' : 'Выберите блок',
                        type: 'select',
                        require: true,
                        title: 'Блок',
                        width: 'w-full',
                        key: 'block',
                        single: true,
                        disabled: isDisabled || !selectedDirection,
                        options: filteredBlocks || [],
                      }}
                      errors={{ block: !!error }}
                    />
                  )}
                  name='block'
                  control={control}
                />
              </div>
            </section>
            <div>
              <Controller
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    dataField={{
                      placeholder: !selectedBlock ? 'Сначала выберите блок' : 'Выберите требование',
                      type: 'select',
                      require: true,
                      title: 'Требование',
                      width: 'w-full',
                      key: 'demand',
                      single: true,
                      disabled: isDisabled || !selectedBlock,
                      options: filteredDemands || [],
                    }}
                    errors={{ demand: !!error }}
                  />
                )}
                name='demand'
                control={control}
              />
            </div>
            {/* <TGButton className='w-full' type='button' icon={<PlusCircleIcon width={20} height={20} />} onClick={openModal}>
              Добавить комментарий
            </TGButton> */}
          </div>
        </form>
        {(actualCommentId || isInitialSaveComplete) && <CommentsDisplay key={commentsRefreshKey} commentId={actualCommentId} />}
        <TGButton className='w-full mt-4' type='button' icon={<PlusCircleIcon width={20} height={20} />} onClick={openModal}>
          Добавить комментарий
        </TGButton>
        <FlexRow className='justify-between mt-10'>
          <TGButton type='button' onClick={cancel}>
            Отмена
          </TGButton>
          <TGButton type='submit' color='bluedark' onClick={handleSubmit(onSubmit)}>
            {lastIdGopComments ? 'Сохранить' : 'Сохранить'}
          </TGButton>
        </FlexRow>
      </FormProvider>
      <ModalAddComment isOpen={isModalOpen} onClose={closeModal} lastId={actualCommentId} />
    </>
  );
}
